// Optional demographic profile stored on a personal account. Option values
// mirror the default instrument demographics_config so answers can be reused.
export const GENDER_OPTIONS = [
  { value: "female", label_id: "Perempuan", label_en: "Female" },
  { value: "male", label_id: "Laki-laki", label_en: "Male" },
  { value: "other", label_id: "Lainnya / tidak ingin menyebut", label_en: "Other / prefer not to say" },
];

export const EDUCATION_OPTIONS = [
  { value: "sma", label_id: "SMA/SMK", label_en: "High school" },
  { value: "d3", label_id: "Diploma", label_en: "Diploma" },
  { value: "s1", label_id: "Sarjana", label_en: "Bachelor's" },
  { value: "s2", label_id: "Magister", label_en: "Master's" },
  { value: "s3", label_id: "Doktoral", label_en: "Doctorate" },
];

export const OCCUPATION_OPTIONS = [
  { value: "student", label_id: "Pelajar / mahasiswa", label_en: "Student" },
  { value: "employed", label_id: "Karyawan / pegawai", label_en: "Employed" },
  { value: "self_employed", label_id: "Wiraswasta / pekerja lepas", label_en: "Self-employed / freelance" },
  { value: "not_working", label_id: "Tidak / belum bekerja", label_en: "Not working" },
  { value: "other", label_id: "Lainnya", label_en: "Other" },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "single", label_id: "Belum menikah", label_en: "Single" },
  { value: "married", label_id: "Menikah", label_en: "Married" },
  { value: "divorced_widowed", label_id: "Cerai / duda / janda", label_en: "Divorced / widowed" },
];

const SELECT_OPTIONS = {
  gender: GENDER_OPTIONS,
  education: EDUCATION_OPTIONS,
  occupation: OCCUPATION_OPTIONS,
  marital_status: MARITAL_STATUS_OPTIONS,
};

export const EMPTY_PROFILE = {
  name: "",
  birth_date: "",
  gender: "",
  education: "",
  occupation: "",
  marital_status: "",
};

// Which account value an instrument demographic field is filled from.
export const PROFILE_SOURCES = ["name", "age", "gender", "education", "occupation", "marital_status"];

const KEY_ALIASES = {
  name: ["name", "nickname", "nama", "nama_panggilan", "full_name"],
  age: ["age", "usia", "umur"],
  gender: ["gender", "jenis_kelamin", "sex"],
  education: ["education", "pendidikan", "pendidikan_terakhir"],
  occupation: ["occupation", "pekerjaan", "job"],
  marital_status: ["marital_status", "status_pernikahan", "status_perkawinan", "marital"],
};

export function profileSource(field) {
  if (field?.profile_key === "none") return null;
  if (PROFILE_SOURCES.includes(field?.profile_key)) return field.profile_key;
  const key = String(field?.key || "").toLowerCase();
  return PROFILE_SOURCES.find((source) => KEY_ALIASES[source].includes(key)) || null;
}

export function accountDisplayName(user) {
  const name = String(user?.name || user?.profile?.name || "").trim();
  if (name) return name;
  return String(user?.email || "").split("@")[0];
}

export function ageFromBirthDate(birthDate, today = new Date()) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate || "");
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  let age = today.getFullYear() - year;
  const beforeBirthday = today.getMonth() + 1 < month
    || (today.getMonth() + 1 === month && today.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
}

function profileValue(source, profile, today) {
  if (source === "age") {
    const age = ageFromBirthDate(profile?.birth_date, today);
    return age === null ? "" : String(age);
  }
  return String(profile?.[source] || "").trim();
}

// Answers an instrument's demographic fields from the account profile. Select
// fields are only answered when the profile value is one of their options.
export function demographicsFromProfile(fields, profile, today = new Date()) {
  const answers = {};
  for (const field of fields || []) {
    const source = profileSource(field);
    if (!source) continue;
    const value = profileValue(source, profile, today);
    if (!value) continue;
    if (field.type === "select" && !(field.options || []).some((option) => option.value === value)) continue;
    answers[field.key] = value;
  }
  return answers;
}

// Profile changes implied by answers given on an instrument's demographics
// step. Age cannot be turned back into a birth date, so it is never saved.
export function profilePatchFromDemographics(fields, answers, profile) {
  const patch = {};
  for (const field of fields || []) {
    const source = profileSource(field);
    if (!source || source === "age") continue;
    const value = String(answers?.[field.key] || "").trim();
    if (!value || value === (profile?.[source] || "")) continue;
    const options = SELECT_OPTIONS[source];
    if (options && !options.some((option) => option.value === value)) continue;
    patch[source] = value;
  }
  return patch;
}

// Shape accepted by the API: blank inputs become null.
export function profilePayload(profile) {
  const payload = {};
  for (const key of Object.keys(EMPTY_PROFILE)) {
    const value = String(profile?.[key] || "").trim();
    payload[key] = value || null;
  }
  return payload;
}

export function profileFormValues(profile) {
  const values = { ...EMPTY_PROFILE };
  for (const key of Object.keys(EMPTY_PROFILE)) values[key] = profile?.[key] || "";
  return values;
}
